import React, {ReactElement} from 'react';
import {companyApi} from "../../backend/CompanyApi";
import {companyAccountsStore} from "../../store/CompanyAccounts";
import LoaderWithCompanySelected from "../../components/LoaderWithCompanySelected";
import {
    Accordion,
    AccordionActions,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Badge,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Snackbar,
    Switch,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {GetCompanyRegDataDto, GetCompanySettingsDto} from "../../model/company/GetCompanySettingsDto";
import {FormikErrors, FormikHelpers, FormikProps} from "formik/dist/types";
import {FieldArray, FieldArrayRenderProps, Formik} from "formik";
import TextFieldWithValidation from "../../components/input/TextFieldWithValidation";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {roleIcon, UserRole} from "../../model/user/UserRole";
import {LinkedUser} from "../../model/user/LinkedUser";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from '@mui/icons-material/Info';
import './CompanySettings.css'
import AddIcon from '@mui/icons-material/Add';
import deepEqual from "deep-equal";
import {SaveCompanySettingsDto} from "../../model/company/SaveCompanySettingsDto";
import {getFileBase64} from "../../utils/ReadFile";
import CountrySelector from "../../components/selector/CountrySelector";
import {BankConnectionStatus, StatementFetchInterval} from "../../model/statement/autofetch/BankConnectionEnums";
import ButtonWithConfirmation from "../../components/button/ButtonWithConfirmation";
import {lhvConnectApi} from "../../backend/LhvConnectApi";
import {AddIbanComponent} from "./settings/AddIbanComponent";
import LoadingButton from "../../components/button/LoadingButton";
import {Link as RouterLink} from "react-router-dom";
import {routes} from "../../main/Routes";
import * as Yup from "yup";
import {observer} from "mobx-react";
import {StatementDownloadIBANSettings} from "../../model/statement/autofetch/StatementDownloadIBANSettings";
import {wiseConnectionApi} from "../../backend/WiseConnectionApi";
import {BillingPlanDto} from "../../model/billing/BillingPlanDto";
import {billingApi} from "../../backend/BillingApi";
import {BillingDataDto} from "../../model/company/BillingDataDto";
import {userInfoStore} from "../../store/UserInfo";
import {SnackbarCloseReason} from "@mui/material/Snackbar/Snackbar";
import {AlertColor} from "@mui/material/Alert/Alert";
import {DatePicker} from "../../components/picker/DatePicker";
import moment from "moment";
import {Trans, Translation} from "react-i18next";
import {logEvent} from "../../utils/SentryUtils";
import {InvoiceDetailsCard} from "./settings/InvoiceDetailsCard";
import {ClientCommunicationCard} from "./settings/ClientCommunicationCard";
import {AiSettingsDetailsCard} from "./settings/AiSettingsDetailsCard";

interface IProps {
    companyId: string;
}

interface IState {
    settings?: GetCompanySettingsDto,
    billingOptions: BillingPlanDto[],
    openSection?: string,
    loading: boolean,
    loadingError?: string,

    snackBar?: {
        messageType: AlertColor,
        messageText: string
    }
}

const GENERAL_COMPANY_DATA_SECTION = "General company data";
const COMPANY_REGISTRATION_DATA_SECTION = "Company registration data";
const MERIT_CONNECTION_SECTION = "Merit connection";
const BILLING_SECTION = "Billing";
const LHV_CONNECT_SECTION = "LHV Connect";
const WISE_SECTION = "Wise connection";
const INVOICE_DETAILS = "Invoice details";
const LINKED_ACCOUNTANTS_SECTION = "Linked accountants";
const LINKED_CLIENTS_SECTION = "Linked clients";
const CLIENT_COMMUNICATION_SECTION = "Client communication details";
const AI_DETAILS_SECTION = "AI details";

const USER_ALLOWED_SECTIONS = new Set([GENERAL_COMPANY_DATA_SECTION, COMPANY_REGISTRATION_DATA_SECTION, INVOICE_DETAILS])

@observer
export default class CompanySettings extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            loading: true,
            billingOptions: []
        };
    }


    async componentDidMount() {
        this.fetchSettings();
        await companyAccountsStore.refresh()
    }

    private fetchSettings = () => {
        this.setState({loading: true})
        const getSettingsPromise = companyApi.fetchSettings(this.props.companyId);
        const billingOptionsPromise = billingApi.fetchOptions(this.props.companyId);

        Promise.all([getSettingsPromise, billingOptionsPromise])
                .then(([settings, billingOptions]) => {
                    this.setState({settings, billingOptions, loading: false})
                })
                .catch(() => this.setState({
                    loading: false,
                    loadingError: 'Failed to load company settings'
                }))
    }

    render() {
        const {loadingError, loading, settings, snackBar} = this.state;
        return (
                <LoaderWithCompanySelected loading={loading} error={loadingError}>
                    <Snackbar open={snackBar !== undefined} autoHideDuration={6000} onClose={this.handleCloseSnackBar}>
                        {snackBar && <Alert onClose={this.handleCloseSnackBar} severity={snackBar.messageType}>
                            {snackBar.messageText}
                        </Alert>}
                    </Snackbar>
                    <Paper>
                        <Formik
                                initialValues={settings!!}
                                onSubmit={this.saveSettings(settings!!)}
                                validationSchema={this.validationSchema}>
                            {(props: FormikProps<GetCompanySettingsDto>) => this.renderChangeRuleForm(props)}
                        </Formik>
                    </Paper>
                </LoaderWithCompanySelected>
        );
    }

    private handleCloseSnackBar = (event: any, reason?: SnackbarCloseReason) => {
        if (reason === 'clickaway') {
            return;
        }

        this.setState({
            snackBar: undefined
        })
    };

    private handleChange = (sectionName: string, props: FormikProps<GetCompanySettingsDto>) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        this.setState({
            openSection: isExpanded ? sectionName : ''
        })
        props.handleReset(event);
        if (isExpanded) {
            logEvent("company_settings.open_section", sectionName);
        }
    };


    private settingsSectionProperties = (name: string, props: FormikProps<GetCompanySettingsDto>) => {
        const openSection = this.state.openSection;

        return {
            expanded: openSection === name,
            onChange: this.handleChange(name, props)
        }
    }

    private section = (label: string, tooltip: string, props: FormikProps<GetCompanySettingsDto>, details: () => ReactElement) => {
        if (userInfoStore.userInfo.currentRole === UserRole.CLIENT && !USER_ALLOWED_SECTIONS.has(label)) {
            return <></>
        }
        const accordionProps = this.settingsSectionProperties(label, props);
        return (
                <Accordion className="settings-section"  {...accordionProps}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>} style={{margin: 0}}>
                        <Typography variant="subtitle2">
                            <Tooltip title={<Trans i18nKey={`company_settings.tabs.${label}.tip`}>{tooltip}</Trans>}>
                                <IconButton size="large"><InfoIcon/></IconButton>
                            </Tooltip>
                            <Trans i18nKey={`company_settings.tabs.${label}.name`}>{label}</Trans>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {accordionProps.expanded && details()}
                    </AccordionDetails>
                    {this.renderActions(props)}
                </Accordion>
        );
    }

    private renderChangeRuleForm(props: FormikProps<GetCompanySettingsDto>) {
        return <Translation>{t =>
                <>
                    {this.section(GENERAL_COMPANY_DATA_SECTION, "Company name, logo...", props,
                            () => <>
                                <Grid container spacing={1}>
                                    <Grid xs={12} item>
                                        <TextFieldWithValidation
                                                autoComplete="off"
                                                name="name"
                                                label={t(`company_settings.general.name`, "Company name")}
                                                placeholder={t(`company_settings.general.name`, "Company name")}
                                                value={props.values.name}
                                                fullWidth={true}
                                                onChange={props.handleChange}
                                                hasError={props.errors.name}
                                                errorText={t(`company_settings.general.name_error`, "Please fill in company name")}
                                        />
                                    </Grid>
                                    <Grid xs={12} container item alignItems="flex-start">
                                        {
                                                props.values.logo &&
                                                <Badge badgeContent={
                                                    <IconButton size="small" color="secondary"
                                                                onClick={() => {
                                                                    logEvent("company_settings", "Logo changed");
                                                                    props.setFieldValue('logo', "");
                                                                }}><CancelIcon/></IconButton>
                                                }>
                                                    <img
                                                            alt={t(`company_settings.general.logo`, "Logo")}
                                                            style={{width: "15rem", height: "15rem"}}
                                                            src={"data:image;base64," + props.values.logo}
                                                            title={t(`company_settings.general.logo`, "Logo")}
                                                    />
                                                </Badge>
                                        }
                                        {
                                                !props.values.logo && <>
                                                    <Typography variant={"subtitle2"}>{t(`company_settings.general.no_logo`, "No logo present")}</Typography>
                                                </>
                                        }
                                    </Grid>
                                    <Grid xs={12} container item alignItems="flex-start">
                                        <TextField type="file" onChange={async (e) => {
                                            // @ts-ignore
                                            const [file] = e.target.files;
                                            if (file) {
                                                const fileContent = await getFileBase64(file);
                                                props.setFieldValue('logo', fileContent);
                                            }
                                        }}/>
                                    </Grid>
                                </Grid>
                            </>
                    )}
                    {this.registrationDataSection(props)}
                    {this.billingSection(props)}
                    {this.section(MERIT_CONNECTION_SECTION, "Merit ApiID and ApiKey", props,
                            () => <>
                                <Grid container spacing={1}>
                                    <Grid xs={12} item>
                                        <TextFieldWithValidation
                                                autoComplete="off"
                                                name="apiId" label="API ID"
                                                placeholder="Merit API ID"
                                                value={props.values.apiId}
                                                fullWidth={true}
                                                onChange={props.handleChange}
                                                hasError={props.errors.apiId}
                                                errorText="Please fill in API ID"
                                        />
                                    </Grid>
                                    <Grid xs={12} item>
                                        <TextFieldWithValidation
                                                autoComplete="off"
                                                name="apiKey" label="API Key"
                                                placeholder="Merit API Key"
                                                value={props.values.apiKey}
                                                fullWidth={true}
                                                onChange={props.handleChange}
                                                hasError={props.errors.apiKey}
                                                errorText="Please fill in API Key"
                                        />
                                    </Grid>
                                </Grid>
                            </>
                    )}

                    {this.section(INVOICE_DETAILS, "Details that will be shown on the sales invoices", props,
                            () => <>
                                <InvoiceDetailsCard formik={props} showSnackBar={this.showSnackBar}/>
                            </>
                    )}

                    {this.section(LHV_CONNECT_SECTION, "LHV connection allows automatically pull bank statements each month", props,
                            () => <>
                                {this.renderLHVConnectSection(props)}
                            </>
                    )}

                    {this.section(WISE_SECTION, "Wise connection allows automatically pull bank statements each month", props,
                            () => <>
                                {this.renderWiseConnectionSection(props)}
                            </>
                    )}

                    <FieldArray name="linkedUsers">
                        {({remove, push}) => (<>
                                    {this.section(LINKED_ACCOUNTANTS_SECTION, "Accountants that can access this company data", props,
                                            () => <>
                                                {this.renderLinkedUserCard(
                                                        props.values.linkedUsers, UserRole.ACCOUNTANT,
                                                        remove, push)
                                                }
                                            </>
                                    )}
                                    {this.section(LINKED_CLIENTS_SECTION, "Clients that can upload documents and generate invoices", props,
                                            () => <>
                                                {this.renderLinkedUserCard(
                                                        props.values.linkedUsers, UserRole.CLIENT,
                                                        remove, push)
                                                }
                                            </>
                                    )}
                                </>
                        )}
                    </FieldArray>
                    {this.section(CLIENT_COMMUNICATION_SECTION, "Communication with client preset", props,
                            () => <>
                                <ClientCommunicationCard formik={props} showSnackBar={this.showSnackBar}/>
                            </>
                    )}
                    {this.section(AI_DETAILS_SECTION, "AI settings details", props,
                            () => <>
                                <AiSettingsDetailsCard formik={props} showSnackBar={this.showSnackBar}/>
                            </>
                    )}
                </>}
        </Translation>
    }

    private registrationDataSection(props: FormikProps<GetCompanySettingsDto>) {
        const clientView = userInfoStore.userInfo.currentRole === UserRole.CLIENT;
        const regData = props.values.registrationData;
        // @ts-ignore
        const errors: FormikErrors<GetCompanyRegDataDto> | undefined = props.errors.registrationData;
        return this.section(COMPANY_REGISTRATION_DATA_SECTION, "Company reg number, address...", props,
                () => <Translation>
                    {t =>
                            <Grid container spacing={1}>
                                <Grid xs={12} item>
                                    <TextFieldWithValidation
                                            disabled={clientView}
                                            autoComplete="off"
                                            name="registrationData.regCode"
                                            label={t(`company_settings.reg_data.reg_code`, "Reg. code")}
                                            placeholder="12345678"
                                            value={regData?.regCode}
                                            fullWidth
                                            onChange={props.handleChange}
                                            hasError={errors?.regCode}
                                            errorText={t(`company_settings.reg_data.reg_code_error`, "Please fill in registration code")}
                                    />
                                </Grid>
                                <Grid xs={12} item>
                                    <TextFieldWithValidation
                                            disabled={clientView}
                                            autoComplete="off"
                                            name="registrationData.vatNumber"
                                            label={t(`company_settings.reg_data.vat_number`, "VAT number")}
                                            value={regData?.vatNumber}
                                            placeholder="EE123456789"
                                            fullWidth={true}
                                            onChange={props.handleChange}
                                    />
                                </Grid>
                                <Grid xs={12} item>
                                    <TextFieldWithValidation
                                            disabled={clientView}
                                            autoComplete="off"
                                            name="registrationData.address"
                                            label={t(`company_settings.reg_data.legal_address`, "Legal Address")}
                                            placeholder="Lossi plats 1a-11"
                                            value={regData?.address}
                                            fullWidth
                                            onChange={props.handleChange}
                                    />
                                </Grid>

                                <Grid xs={6} item>
                                    <TextFieldWithValidation
                                            disabled={clientView}
                                            autoComplete="off"
                                            name="registrationData.city"
                                            label={t(`company_settings.reg_data.city`, "City")}
                                            placeholder="Tallinn"
                                            value={regData?.city}
                                            fullWidth
                                            onChange={props.handleChange}
                                    />
                                </Grid>
                                <Grid xs={6} item>
                                    <TextFieldWithValidation
                                            disabled={clientView}
                                            autoComplete="off"
                                            name="registrationData.postalCode"
                                            label={t(`company_settings.reg_data.post_code`, "Postal code")}
                                            placeholder="10137"
                                            value={regData?.postalCode}
                                            fullWidth
                                            onChange={props.handleChange}
                                            hasError={errors?.postalCode}
                                    />
                                </Grid>
                                <Grid xs={12} item>
                                    <CountrySelector
                                            disabled={clientView}
                                            label={t(`company_settings.reg_data.country`, "Country")}
                                            placeholder={t(`company_settings.reg_data.country`, "Country")}
                                            value={regData?.countryCode}
                                            hasError={
                                                // @ts-ignore
                                                errors?.countryCode
                                            }
                                            errorText={t(`company_settings.reg_data.country_error`, "Please fill in registration country")}
                                            onChanged={code => props.setFieldValue("registrationData.countryCode", code, true)}

                                    />
                                </Grid>
                                <Grid xs={6} item>
                                    <TextFieldWithValidation
                                            autoComplete="off"
                                            name="registrationData.email"
                                            label={t(`company_settings.reg_data.email`, "Email")}
                                            placeholder="info@company.com"
                                            value={regData?.email}
                                            fullWidth
                                            onChange={props.handleChange}
                                            hasError={errors?.email}
                                            errorText={t(`company_settings.reg_data.email_error`, "Email address is invalid")}
                                    />
                                </Grid>
                                <Grid xs={6} item>
                                    <TextFieldWithValidation
                                            autoComplete="off"
                                            name="registrationData.phoneNo"
                                            label={t(`company_settings.reg_data.phone_num`, "Phone number")}
                                            placeholder="+372 5555 8888"
                                            value={regData?.phoneNo}
                                            fullWidth
                                            onChange={props.handleChange}
                                    />
                                </Grid>
                            </Grid>
                    }
                </Translation>
        );
    }


    private billingSection(props: FormikProps<GetCompanySettingsDto>) {
        const billingOptions = this.state.billingOptions;
        const billing = props.values.billing;
        // @ts-ignore
        const errors: FormikErrors<BillingDataDto> | undefined = props.errors.billing;
        return this.section(BILLING_SECTION, "Billing plan, enforcement date, subscription info", props,
                () => <>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Autocomplete
                                    id="billing.subscriptionPlan"
                                    aria-required
                                    isOptionEqualToValue={(o, v) => o?.planId === v?.planId}
                                    value={billingOptions.find(it => it.planId === billing?.subscriptionPlan) ?? null}
                                    options={billingOptions}
                                    getOptionLabel={(option) => `${option.name} @ ${option.price}${option.currency}/${option.recurringInterval}`}
                                    renderInput={(params) => <>
                                        <TextFieldWithValidation {...params}
                                                                 label="Subscription plan"
                                                                 hasError={errors?.subscriptionPlan}
                                                                 autoComplete="off"
                                        />
                                    </>}
                                    onChange={(e, value) => {
                                        const newVar = value ? value.planId : null;
                                        props.setFieldValue("billing.subscriptionPlan", newVar, true)
                                    }}
                            />
                        </Grid>
                        <Grid item container xs={12} alignItems={"center"}>
                            <Grid item>
                                <DatePicker
                                        label="Enforce subscription from"
                                        clearable={true}
                                        value={billing?.enforceSubscriptionFrom ? moment(billing.enforceSubscriptionFrom) : null}
                                        onChange={date => props.setFieldValue("billing.enforceSubscriptionFrom", date?.toDate(), true)}
                                        error={errors?.enforceSubscriptionFrom}
                                />
                            </Grid>
                            <Grid item>
                                <Tooltip title={"Starting from this date, if client does not pay for subscription access to the system will be blocked"}>
                                    <IconButton style={{padding: 1, marginTop: "12px"}} size="large">
                                        <InfoIcon/>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </>
        );
    }

    private renderLHVConnectSection(props: FormikProps<GetCompanySettingsDto>) {
        const registrationData = props.values.registrationData;
        const status = props.values.lhvConnect.status;
        if (registrationData?.regCode && registrationData?.countryCode) {
            const disableSettingsEdit = status !== BankConnectionStatus.ACTIVE;
            return <>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12}>
                        {status === BankConnectionStatus.NOT_ACTIVE &&
                                <Alert severity="info">Would you like to activate LHV Connect for this
                                    company?
                                    <br/>
                                    <ButtonWithConfirmation onClick={this.enrollCompany} color="primary" variant="contained">
                                        Send contract to company owner
                                    </ButtonWithConfirmation>
                                </Alert>}

                        {status === BankConnectionStatus.PENDING &&
                                <Alert severity="info">Contract was sent to client, but no confirmation from bank yet. <br/>
                                    <b>Please ask client to sign the contract and send it back to the bank.</b>
                                    {this.renderCheckLhvStateButton(props)}</Alert>}

                        {status === BankConnectionStatus.ACTIVE &&
                                <Alert severity="success">Connection is ACTIVE
                                    (<Link component={RouterLink} to={routes.lhvConnect} underline="hover">check logs</Link>)
                                    {this.renderCheckLhvStateButton(props)}</Alert>}

                        {status === BankConnectionStatus.ERROR &&
                                <Alert severity="error">Error occurred contacting the bank
                                    {this.renderCheckLhvStateButton(props)}</Alert>}
                    </Grid>
                    <Grid item xs={12}><Divider/></Grid>
                    {disableSettingsEdit && <>
                        <Grid item xs={12}><Alert severity="warning">You can't edit other settings until bank confirms
                            connection</Alert>
                        </Grid>
                    </>}
                    <Grid item xs={12}>
                        <TextFieldWithValidation
                                disabled={disableSettingsEdit}
                                select
                                name="lhvConnect.fetchInterval"
                                label="Period"
                                placeholder="Statement download interval"
                                value={props.values.lhvConnect.fetchInterval}
                                fullWidth={true}
                                onChange={props.handleChange}
                        >
                            {this.getStatementDownloadIntervalOptions()}
                        </TextFieldWithValidation>
                    </Grid>
                    <FieldArray name="lhvConnect.ibans"
                                render={(arrHelper) => {
                                    return this.renderIbanSection(props.values.lhvConnect.ibans, disableSettingsEdit, arrHelper);
                                }}
                    />
                </Grid>
            </>;
        } else {
            return (
                    <Alert severity="error">
                        <Typography variant="subtitle2"> Company <b>registration number</b> and <b>country</b> should be
                            <span> <Link
                                    onClick={() => this.setState({openSection: COMPANY_REGISTRATION_DATA_SECTION})}
                                    underline="hover">
                                            filled-in</Link></span> before LHV Connect activation.</Typography>
                    </Alert>
            );
        }
    }

    private getStatementDownloadIntervalOptions = () => {
        return [
            (<MenuItem key={StatementFetchInterval.DONT_FETCH}
                       value={StatementFetchInterval.DONT_FETCH}>Don't download statement automatically
            </MenuItem>),
            (<MenuItem key={StatementFetchInterval.DAILY_WITH_WEEKLY_SUMMARY}
                       value={StatementFetchInterval.DAILY_WITH_WEEKLY_SUMMARY}>Download daily, but save only weekly statement</MenuItem>),
            (<MenuItem key={StatementFetchInterval.WEEKLY}
                       value={StatementFetchInterval.WEEKLY}>Download statement weekly</MenuItem>),
            (<MenuItem key={StatementFetchInterval.MONTHLY}
                       value={StatementFetchInterval.MONTHLY}>Download statement monthly</MenuItem>),
        ]
    }

    private renderIbanSection(ibans: StatementDownloadIBANSettings[], disableSettingsEdit: boolean, arrHelper: FieldArrayRenderProps) {
        return <>
            <Grid item xs={1}>
                <Typography variant="caption">
                    Only statement download
                </Typography>
            </Grid>
            <Grid container item xs={11}>
                {this.renderIbansList(ibans, disableSettingsEdit, arrHelper, false)}
            </Grid>

            <Grid item xs={1}>
                <Typography variant="caption">
                    Automatic analysis
                </Typography>
            </Grid>
            <Grid container item xs={11}>
                {this.renderIbansList(ibans, disableSettingsEdit, arrHelper, true)}
            </Grid>
            <Grid item xs={12}><Divider/></Grid>
            <Grid container item xs={12}>
                <AddIbanComponent disableSettingsEdit={disableSettingsEdit}
                                  arrHelper={arrHelper} ibans={ibans}
                                  showSnackBar={this.showSnackBar}/>
            </Grid>
        </>
    }

    private renderWiseConnectionSection(props: FormikProps<GetCompanySettingsDto>) {
        const status = props.values.wiseConnection.status;
        return <>
            <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12}>
                    <TextFieldWithValidation
                            name="wiseConnection.token"
                            label="Token"
                            placeholder="Wise API token"
                            value={props.values.wiseConnection.token}
                            fullWidth={true}
                            onChange={props.handleChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    {status === BankConnectionStatus.NOT_ACTIVE &&
                            <Alert severity="info">Would you like to activate connection with Wise?
                                <br/>
                                {this.renderCheckWiseStateButton(props)}
                            </Alert>}

                    {status === BankConnectionStatus.ACTIVE &&
                            <Alert severity="success">Connection is ACTIVE
                                (<Link component={RouterLink} to={routes.wiseConnection} underline="hover">check logs</Link>)
                                {this.renderCheckWiseStateButton(props)}</Alert>}

                    {status === BankConnectionStatus.ERROR &&
                            <Alert severity="error">Error occurred contacting the bank
                                {this.renderCheckLhvStateButton(props)}</Alert>}
                </Grid>
                <Grid item xs={12}><Divider/></Grid>
                <Grid item xs={12}>
                    <TextFieldWithValidation
                            select
                            name="wiseConnection.fetchInterval"
                            label="Period"
                            placeholder="Statement download interval"
                            value={props.values.wiseConnection.fetchInterval}
                            fullWidth={true}
                            onChange={props.handleChange}
                    >
                        {this.getStatementDownloadIntervalOptions()}
                    </TextFieldWithValidation>
                </Grid>
                <FieldArray name="wiseConnection.ibans"
                            render={(arrHelper) => {
                                return this.renderIbanSection(props.values.wiseConnection.ibans, false, arrHelper);
                            }}
                />
            </Grid>
        </>;
    }


    private renderIbansList(ibans: StatementDownloadIBANSettings[], disableSettingsEdit: boolean, arrHelper: FieldArrayRenderProps, analyzeAutomatically: boolean) {
        if (ibans) {
            const filteredIbans = ibans.filter(it => it.analyzeAutomatically === analyzeAutomatically);
            if (filteredIbans && filteredIbans.length !== 0) {
                return <>
                    {filteredIbans.map((it, index) => {
                        return <Chip disabled={disableSettingsEdit} key={index}
                                     label={<FormControlLabel
                                             control={<Switch disabled={disableSettingsEdit}
                                                              checked={it.analyzeAutomatically}
                                                              onClick={() => {
                                                                  arrHelper.remove(ibans.indexOf(it))
                                                                  arrHelper.push({
                                                                      ...it,
                                                                      analyzeAutomatically: !analyzeAutomatically
                                                                  })
                                                              }}
                                                              color="primary"/>}
                                             label={it.iban + " - " + it.currency}
                                             style={{marginRight: "0"}}/>}
                                     onDelete={() => {
                                         arrHelper.remove(ibans.indexOf(it))
                                     }}
                        />
                    })}
                </>;
            }
        }
        return <Typography variant="subtitle2">No linked IBANs</Typography>
    }

    private renderCheckLhvStateButton(props: FormikProps<GetCompanySettingsDto>) {
        return <>
            <br/>
            <LoadingButton disabled={props.dirty} onClick={this.checkConnectionWithLHV(props)}
                           color="primary" variant="contained">
                Check connection with the bank
            </LoadingButton>
        </>
    }

    private renderCheckWiseStateButton(props: FormikProps<GetCompanySettingsDto>) {
        return <>
            <br/>
            <LoadingButton disabled={props.dirty} onClick={this.checkConnectionWithWise(props)}
                           color="primary" variant="contained">
                Check connection with the bank
            </LoadingButton>
        </>
    }

    private enrollCompany = async () => {
        try {
            await lhvConnectApi.enrollCompany()
            this.showSnackBar("success", "Client should receive LHV Connect contract via email");
            this.fetchSettings()
        } catch (e) {
            this.showSnackBar("error", "Failed to enroll company, please contact support");
            throw e
        }
    };

    private checkConnectionWithLHV = (props: FormikProps<GetCompanySettingsDto>) => async () => {
        await this.doCheckBankStatus(lhvConnectApi.checkStatus(), props.values.lhvConnect.status);
    }

    private async doCheckBankStatus(statusUpdate: Promise<{ status: BankConnectionStatus; error?: string }>, currentStatus: BankConnectionStatus) {
        const {status, error} = await statusUpdate;
        if (error) {
            this.showSnackBar("error", "Bank returned an error: " + error);
            if (currentStatus !== status) {
                this.fetchSettings();
            }
        } else {
            if (currentStatus === status) {
                this.showSnackBar("info", "No updates");
            } else {
                this.showSnackBar("info", "Received status updates");
                this.fetchSettings()
            }
        }
    }

    private checkConnectionWithWise = (props: FormikProps<GetCompanySettingsDto>) => async () => {
        await this.doCheckBankStatus(wiseConnectionApi.checkStatus(), props.values.wiseConnection.status);
    }

    private showSnackBar = (messageType: AlertColor, messageText: string) => {
        this.setState({
            snackBar: {
                messageType: messageType,
                messageText: messageText
            }
        })
    }

    private renderActions = (props: FormikProps<GetCompanySettingsDto>) => {
        return <AccordionActions style={{justifyContent: "flex-start"}}>
            <LoadingButton onClick={props.submitForm} variant={"outlined"} startIcon={<SaveAltIcon/>}
                           color="primary" disabled={!props.dirty || props.isSubmitting}><Trans i18nKey="common.save">Save</Trans></LoadingButton>
            <Button color="secondary" variant={"outlined"} onClick={props.handleReset}
                    startIcon={<CancelIcon/>} disabled={!props.dirty || props.isSubmitting}><Trans i18nKey="common.cancel">Cancel</Trans></Button>
        </AccordionActions>;
    }

    private renderLinkedUserCard = (linkedUsers: LinkedUser[], type: UserRole,
                                    remove: (index: number) => void, push: (obj: LinkedUser) => void) => {
        const hasLinkedUsers = linkedUsers.filter(it => it.type === type).length > 0;
        return <>
            <List dense>
                {!hasLinkedUsers ?
                        <>
                            <ListItem>
                                <ListItemText primary={<Typography variant={"h6"}>No one linked yet</Typography>}/>
                            </ListItem>
                        </>
                        :
                        <>
                            {
                                linkedUsers
                                        .map((it, index) => {
                                            if (it.type === type) {
                                                return (
                                                        <ListItem key={index}>
                                                            <ListItemText primary={<Chip
                                                                    icon={roleIcon(type)}
                                                                    label={it.email}
                                                                    onDelete={() => remove(index)}
                                                                    color="default"/>
                                                            }/>
                                                        </ListItem>
                                                );
                                            } else {
                                                return undefined
                                            }
                                        })
                                        .filter(it => it !== undefined)
                            }
                        </>
                }
                <ListItem>
                    {this.renderAddItemButton(type, push)}
                </ListItem>
            </List>
        </>;
    }

    private renderAddItemButton(type: UserRole, push: (obj: LinkedUser) => void) {
        const ref: React.RefObject<any> = React.createRef();
        return <ListItemText
                primary={
                    <>
                        <Grid container spacing={1} alignItems="flex-end">
                            <Grid item>
                                {roleIcon(type)}
                            </Grid>

                            <Grid item>
                                <TextField inputRef={ref} type="email" label="Link new"/>
                            </Grid>
                            <Grid item>
                                <IconButton size={"small"} color="primary" onClick={() => {
                                    const newEmail = ref.current.value;
                                    if (newEmail) {
                                        push({type: type, email: newEmail})
                                        ref.current.value = '';
                                    }
                                }}>
                                    <AddIcon/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    </>
                }
        />;
    }

    private saveSettings = (initialValues: GetCompanySettingsDto) => async (changed: GetCompanySettingsDto, formikHelpers: FormikHelpers<GetCompanySettingsDto>) => {
        const newSettings: SaveCompanySettingsDto = {};
        const props = Object.keys(changed);
        for (let prop of props) {
            // @ts-ignore
            if (!deepEqual(initialValues[prop], changed[prop])) {
                // @ts-ignore
                newSettings[prop] = changed[prop];
            }
        }
        try {
            const updatedSettings = await companyApi.saveSettings(this.props.companyId, newSettings)
            await userInfoStore.refreshCompanies();
            this.setState({
                settings: updatedSettings,
                snackBar: {
                    messageType: "success",
                    messageText: "Settings were saved"
                }
            })
            formikHelpers.resetForm({values: changed});
        } catch (e) {
            this.setState({
                snackBar: {
                    messageType: "error",
                    messageText: "Failed to save settings"
                }
            })

            throw e;
        }
    }

    private validationSchema = Yup.lazy((values: GetCompanySettingsDto) => {
        const openSection = this.state.openSection;
        let scheme = {};
        if (openSection === GENERAL_COMPANY_DATA_SECTION) {
            scheme = {
                name: Yup.string().trim().required(),
            };
        }

        if (openSection === MERIT_CONNECTION_SECTION) {
            scheme = {
                apiId: Yup.string().trim().required(),
                apiKey: Yup.string().trim().required(),
            };
        }

        if (openSection === COMPANY_REGISTRATION_DATA_SECTION) {
            scheme = {
                registrationData: Yup.object({
                    regCode: Yup.string().trim().required(),
                    countryCode: Yup.string().trim().required(),
                    email: Yup.string().email(),
                }),
            };
        }

        if (openSection === INVOICE_DETAILS && values.invoiceData?.invoiceAddress) {
            scheme = {
                invoiceData: Yup.object({
                    invoiceAddress: Yup.object({
                        countryCode: Yup.string().trim().required(),
                        email: Yup.string().email(),
                    })
                }),
            }
        }

        if (openSection === BILLING_SECTION) {
            scheme = {
                billing: Yup.object({
                    enforceSubscriptionFrom: values.billing?.subscriptionPlan ?
                            Yup.string().required("In case subscription plan is specified the date is mandatory") :
                            Yup.string().max(0, "In case subscription plan is not specified the date should be empty"),
                }),
            };
        }


        return Yup.object(scheme);
    });
}
